// ─── Session-only WhatsApp-tap store ─────────────────────────────────────────
//
// This is a deliberately tiny, dependency-free module. It holds the module-level
// tap record and the `registerWhatsAppTap` API + `voom:whatsapp-tap` event
// contract that ProductCard and the app-root listener depend on.
//
// It is kept SEPARATE from TapFeedbackModal.tsx (which pulls in the heavy Radix
// Sheet/Dialog) so that the always-mounted app-root listener can import the
// store and `registerWhatsAppTap` without dragging the modal — and its Radix
// dependencies — into the critical-path entry chunk. The modal itself is only
// loaded after a buyer actually taps WhatsApp.

export interface TapRecord {
  productId: number;
  vendorName: string;
  timestamp: number;
  shown: boolean;
}

let _lastTap: TapRecord | null = null;

/** Read the current pending tap record (or null). */
export function getLastTap(): TapRecord | null {
  return _lastTap;
}

/** Mark the current tap as shown so it doesn't reappear. */
export function markTapShown(): void {
  if (_lastTap) _lastTap.shown = true;
}

/** True if there is a tap that hasn't been surfaced to the user yet. */
export function hasPendingTap(): boolean {
  return _lastTap !== null && !_lastTap.shown;
}

/**
 * Register a WhatsApp tap. Call this from ProductCard (or anywhere a WhatsApp
 * tap fires). The TapFeedbackModal listens for `visibilitychange` and checks
 * whether 3+ minutes have elapsed since this registration.
 *
 * Also dispatches a DOM event so the app-root listener can lazy-mount the modal
 * (and the modal itself can be notified) without polling.
 */
export function registerWhatsAppTap(productId: number, vendorName: string) {
  _lastTap = { productId, vendorName, timestamp: Date.now(), shown: false };
  // Dispatch a custom event so the listener/modal can pick it up even if they
  // mount after the tap (e.g. lazy-loaded modal chunk).
  window.dispatchEvent(
    new CustomEvent("voom:whatsapp-tap", {
      detail: { productId, vendorName },
    })
  );
}
